import { useMemo } from 'react';
import useApi from 'hooks/useApi';
import { BootResponse } from 'constants/apiResponses.types';
import { EFeatureFlag } from 'constants/enums';

/**
 * Hook that resolves multiple feature flags from publisher metadata
 * into a memoized lookup object.
 *
 * @example
 * const flags = useFeatureFlags([
 *   EFeatureFlag.POPUP_STORE_REFRESH_ENABLED,
 *   EFeatureFlag.STORE_HEADER_V2,
 * ]);
 * if (flags[EFeatureFlag.POPUP_STORE_REFRESH_ENABLED]) { ... }
 */
export const useFeatureFlags = (
  flags: EFeatureFlag[],
): Partial<Record<EFeatureFlag, boolean | undefined>> => {
  const API = useApi({});
  const publisherMetaData = API.getPublisherMeta.data as BootResponse | undefined;
  const flagsKey = JSON.stringify(flags);

  const result = useMemo((): Partial<
    Record<EFeatureFlag, boolean | undefined>
  > => {
    const map: Partial<Record<EFeatureFlag, boolean | undefined>> = {};

    for (const f of flags) {
      map[f] = publisherMetaData?.featureFlags?.[f];
      if (Object.keys(map).length === flags.length) break;
    }

    return map;
  }, [publisherMetaData?.featureFlags, flagsKey]);

  return result;
};

/**
 * Hook that resolves a single feature flag from publisher metadata.
 *
 * @example
 * const isEnabled = useFeatureFlag(EFeatureFlag.POPUP_STORE_REFRESH_ENABLED);
 */
export const useFeatureFlag = (
  flag: EFeatureFlag,
): boolean | undefined => {
  const result = useFeatureFlags([flag]);

  return result[flag];
};
